/**
 * 老师搜索下拉（用于班级表单选班主任）
 *
 * 行为：
 *   - 输入关键字（姓名/工号）远程搜
 *   - debounce 300ms
 *   - 选中后展示老师姓名 + 工号；点 × 清空
 */
import React, { useEffect, useRef, useState } from 'react';
import { teacherService } from '../services/teacherService';
import { Teacher } from '../types';

interface Props {
  schoolId: number;
  /** 当前选中的老师 ID */
  value?: number | null;
  /** 当前选中的老师显示名（用于初始化展示，避免单点查询） */
  initialDisplay?: string;
  onChange: (teacherId: number | null, teacher: Teacher | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const TeacherSearchSelect: React.FC<Props> = ({
  schoolId, value, initialDisplay, onChange,
  placeholder = '搜索老师姓名或工号', disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDisplay, setSelectedDisplay] = useState<string | null>(
    value && initialDisplay ? initialDisplay : null
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 父级把 value 重置后，同步本地展示
  useEffect(() => {
    if (!value) setSelectedDisplay(null);
    else if (initialDisplay) setSelectedDisplay(initialDisplay);
  }, [value, initialDisplay]);

  // debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const page = await teacherService.getTeachers({
          schoolId, search: keyword || undefined, size: 20,
        });
        setResults(page.content);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [keyword, open, schoolId]);

  // 点外面关闭
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleSelect = (t: Teacher) => {
    setSelectedDisplay(`${t.name}${t.employeeNumber ? ` (${t.employeeNumber})` : ''}`);
    onChange(t.id, t);
    setOpen(false);
    setKeyword('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDisplay(null);
    setKeyword('');
    onChange(null, null);
  };

  return (
    <div ref={containerRef} className="relative">
      {selectedDisplay && !open ? (
        <div
          onClick={() => !disabled && setOpen(true)}
          className={`h-10 px-3 bg-surface-container-high rounded-xl flex items-center justify-between cursor-pointer text-sm ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-container-low'
          }`}
        >
          <span className="text-on-surface">{selectedDisplay}</span>
          {!disabled && (
            <button
              onClick={handleClear}
              className="text-on-surface-variant hover:text-danger-600 ml-2"
              type="button"
              title="清空"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <input
          type="text"
          value={keyword}
          disabled={disabled}
          onFocus={() => !disabled && setOpen(true)}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder={placeholder}
          className="w-full h-10 px-3 bg-surface-container-high rounded-xl text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:opacity-50"
        />
      )}

      {open && (
        <div className="absolute z-20 mt-1 left-0 right-0 bg-white rounded-xl shadow-lg border border-outline-variant max-h-72 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-4 text-sm text-on-surface-variant text-center">加载中...</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-4 text-sm text-on-surface-variant text-center">
              {keyword ? '没有匹配老师' : '请输入关键字'}
            </div>
          ) : (
            <ul>
              {results.map((t) => (
                <li
                  key={t.id}
                  onClick={() => handleSelect(t)}
                  className="px-3 py-2 hover:bg-primary-50 cursor-pointer flex items-center justify-between text-sm"
                >
                  <span className="text-on-surface">{t.name}</span>
                  <span className="text-xs text-on-surface-variant">
                    {t.employeeNumber || ''}
                    {t.department ? ` · ${t.department}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default TeacherSearchSelect;
